import { z } from 'zod';
import { CLOCK_EVENT_TYPES } from '../enums';

/**
 * GPS coordinates — REQUIRED for clock events (per project decision: GPS mode B).
 *
 * `accuracy` is the radius (meters) of confidence around lat/lng,
 * as reported by the browser's Geolocation API. We log it for audit
 * purposes but do NOT reject low-accuracy reads — that's better
 * handled as a soft warning in the UI.
 */
export const gpsCoordsSchema = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
  /** Reported accuracy in meters (lower = more precise). */
  accuracy: z.number().min(0).max(100000),
});
export type GpsCoords = z.infer<typeof gpsCoordsSchema>;

/**
 * Clock-in / clock-out event submitted by client.
 *
 * Server records its own authoritative timestamp + IP + Cloudflare-derived
 * city/country at write-time. Client-provided `clientTs` is logged for
 * audit (e.g. to detect clock skew or replay attempts).
 */
export const clockEventInputSchema = z.object({
  eventType: z.enum(CLOCK_EVENT_TYPES),
  /** Client-reported timestamp (informational — server uses its own clock). */
  clientTs: z.string().datetime(),
  /** REQUIRED — server rejects requests without valid GPS. */
  gps: gpsCoordsSchema,
  /** Optional reference to a known office/site location. */
  locationId: z.string().uuid().optional(),
  /** Free-text note (e.g. "client site visit"). */
  note: z.string().max(500).optional(),
});
export type ClockEventInput = z.infer<typeof clockEventInputSchema>;

/**
 * Returned to client after successful clock event.
 * Includes the server-derived fields so the client can display correctly.
 */
export const clockEventResponseSchema = z.object({
  id: z.string().uuid(),
  eventType: z.enum(CLOCK_EVENT_TYPES),
  serverTs: z.string().datetime(),
  gps: gpsCoordsSchema,
  cfCountry: z.string().nullable(),
  cfCity: z.string().nullable(),
  ipAddress: z.string().nullable(),
});
export type ClockEventResponse = z.infer<typeof clockEventResponseSchema>;
